import React from 'react';
import { ChevronLeft, ChevronRight, MoreHorizontal } from 'lucide-react';

/**
 * Reusable Pagination Component
 * @param {Object} props
 * @param {number} props.currentPage - Current active page
 * @param {number} props.totalPages - Total number of pages
 * @param {function} props.onPageChange - Callback when page changes
 */
const Pagination = ({ currentPage, totalPages, onPageChange }) => {
  if (totalPages <= 1) return null;

  const renderPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Logic for windowed pagination: 1 ... [current-1, current, current+1] ... total
      if (currentPage <= 3) {
        pages.push(1, 2, 3, 4, '...', totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1, '...', totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
      } else {
        pages.push(1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages);
      }
    }

    return pages.map((page, index) => {
      if (page === '...') {
        return (
          <span key={`dots-${index}`} className="flex items-center justify-center w-9 h-9 text-gray-400 dark:text-gray-500">
            <MoreHorizontal className="w-4 h-4" />
          </span>
        );
      }

      return (
        <button
          key={page}
          onClick={() => onPageChange(page)}
          className={`w-9 h-9 flex items-center justify-center rounded-lg text-sm font-medium transition-all duration-200 ${
            currentPage === page
              ? 'bg-[#2563EB] text-white shadow-sm shadow-[#2563EB]/20'
              : 'text-[#64748B] dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-[#31373f] hover:text-[#1E293B] dark:hover:text-gray-200'
          }`}
        >
          {page}
        </button>
      );
    });
  };

  return (
    <div className="flex items-center justify-between px-6 py-4 border-t border-[#E2E8F0] dark:border-gray-800 bg-white dark:bg-[#262b33]">
      <div className="text-[13px] text-[#64748B] dark:text-gray-400">
        Showing page <span className="font-medium text-[#1E293B] dark:text-gray-200">{currentPage}</span> of <span className="font-medium text-[#1E293B] dark:text-gray-200">{totalPages}</span>
      </div>
      
      <div className="flex items-center gap-1">
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="p-2 mr-2 rounded-lg text-[#64748B] dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-[#31373f] disabled:opacity-40 disabled:hover:bg-transparent transition-all"
          title="Previous Page"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>

        <div className="flex items-center gap-1">
          {renderPageNumbers()}
        </div>

        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="p-2 ml-2 rounded-lg text-[#64748B] dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-[#31373f] disabled:opacity-40 disabled:hover:bg-transparent transition-all"
          title="Next Page"
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
};

export default Pagination;
